import type { ReactElement, ReactNode } from "react";
import { InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Spinner } from "../ui-kit";
import { readConfig } from "../config";
import { ConfigurationRequired } from "../shell/ConfigurationRequired";
import { SignInView } from "./SignInView";

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * The route guard for the two protected data views (ISSUE-329 Deliverable C,
 * step 6). A signed-out visitor never reaches `children` — only `SignInView`
 * — and `children` is the sole authenticated-but-no-data-yet placeholder
 * until Deliverable D replaces it with the real fixture-backed view.
 *
 * Renders the configuration-error screen itself when the runtime
 * configuration is incomplete — this is the app's sole auth boundary, so it
 * must deny here rather than delegate the decision to whichever view it
 * wraps (CWE-636 fail-open, security-review finding on ISSUE-329 Deliverable
 * C). `AuthProvider` did not construct a real MSAL instance in that case, so
 * `useIsAuthenticated()` reads `@azure/msal-react`'s stub context (always
 * signed-out) — showing `SignInView` there would offer a sign-in that cannot
 * work.
 */
export function RequireAuth({ children }: RequireAuthProps): ReactElement {
  const config = readConfig();
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  if (!config.ok) {
    return <ConfigurationRequired missing={config.missing} />;
  }
  if (inProgress !== InteractionStatus.None) {
    return <Spinner label="Checking your sign-in status…" />;
  }
  if (!isAuthenticated) {
    return <SignInView />;
  }
  return <>{children}</>;
}
